import { Plus } from "lucide-react";
import { useState } from "react";

import { QueryErrorState } from "@/api/QueryErrorState";
import { queryKeys } from "@/api/hooks";
import { isTerminalQueryError } from "@/api/queryClient";
import { useLibraryAssetsQuery } from "@/api/library";
import {
  useSummaryTargetCatalogQuery,
  type SummaryPromptAsset,
  type SummaryTargetCatalog,
} from "@/api/summaryTargets";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import { useCapability } from "@/auth/SessionContext";
import { formatDate } from "@/lib/format";
import { libraryAssetHref } from "@/lib/librarySections";
import {
  evaluationsOfGroup,
  summaryTargetName,
} from "@/lib/summaryTargets";
import { Button } from "@/ui/Button";
import { Card } from "@/ui/Card";
import { Tooltip } from "@/ui/Tooltip";
import {
  EmptyState,
  LoadingState,
  PartialDataNotice,
  StaleDataNotice,
} from "@/ui/states";

import { AssetStateBadges } from "../AssetStateBadges";
import { CatalogGroup } from "../CatalogGroup";
import { CatalogDetails, CatalogRow } from "../CatalogRow";
import { LibrarySectionHeader } from "../LibrarySectionHeader";
import { NamedReadFailureState } from "./NamedReadFailure";
import { NewSummaryPromptDialog } from "./NewSummaryPromptDialog";

/**
 * The Summary section of the shared clinical library (#863).
 *
 * ## WHAT IT REPLACES
 *
 * The generic `LibraryCatalog`, which listed Summary prompts as undifferentiated
 * assets and offered `NewAssetDialog` — a Type picker across nine families, a
 * parts editor with a free-text "Part key" input, and a free-text Pack placement
 * box. For this family that last field is the observed gap: the committed
 * MOD/NII equivalence contract forbids a free-form nested path, and the field
 * cannot express the depression target at all.
 *
 * ## TWO GROUPS, BECAUSE THERE ARE TWO CLINICAL SHAPES
 *
 * The Lovable reference lists eight rows in one table and includes
 * `diagnostic_findings` among them as though it were an ordinary section. It is
 * not: the summary agent DESCENDS into that directory and opens
 * `<group>/<evaluation>/<evaluation>.txt` inside it, and the service refuses a
 * flat prompt there outright. Rendering the reference's row would be rendering a
 * target that cannot exist. So the section keeps the reference's compact row and
 * its metadata line, and splits them under the two headings the shapes actually
 * have — the `CatalogGroup` shell #908 published for exactly this.
 *
 * ## THE TARGET IS THE FIRST FACT ON EVERY ROW
 *
 * A Summary prompt's NAME is what a clinician called it; its TARGET is which
 * prompt it is. The reference's one column is the target slug, and this keeps
 * that: the slug leads the metadata line in monospace, and Details carries the
 * exact pack directory the freeze writes — the server's own expansion, never a
 * path this application spells.
 */

/** Rows shown per group before the "Show more" control, bounding the page. */
const PAGE_SIZE = 20;

export function SummaryCatalog({
  sectionLabel,
  sectionDescription,
}: {
  sectionLabel: string;
  /** The section's one sentence, declared beside its rail entry (#908). */
  sectionDescription: string;
}) {
  const [creating, setCreating] = useState(false);
  const [copying, setCopying] = useState<SummaryPromptAsset | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const assets = useLibraryAssetsQuery(showArchived);
  const targets = useSummaryTargetCatalogQuery();
  const write = useCapability("library.write");

  const retryAssets = useRetryPausedQuery(queryKeys.libraryAssets(showArchived));
  const retryTargets = useRetryPausedQuery(queryKeys.summaryTargetCatalog);

  // NARROWED, not merely filtered: `LibraryAsset` is discriminated on
  // `assetType` and only the Summary member carries `summaryTarget`, so the
  // predicate is what lets every row below read a target at all.
  const items = (assets.data?.items ?? []).filter(
    (asset): asset is SummaryPromptAsset => asset.assetType === "summary_prompt",
  );
  const sections = items.filter(
    (asset) => asset.summaryTarget?.kind === "section",
  );
  const diagnostics = items.filter(
    (asset) => asset.summaryTarget?.kind === "diagnostic",
  );
  // A row whose stored coordinate is not a shape this family admits reads back
  // with a null target. It is not hidden and not silently sorted into one of the
  // two groups: which prompt it is cannot be stated, and that is the fact.
  const unreadable = items.filter((asset) => !asset.summaryTarget);

  // The target vocabulary is a dependency of the DETAILS column and of the
  // create control, never of the rows: a row's target comes from the asset. So
  // a failed catalog read degrades those two and leaves the list intact.
  const targetsFailure = targets.isPaused
    ? targets.failureReason
    : targets.isError
      ? targets.error
      : null;
  const targetsUnread = targets.isPaused || targets.isError;

  const createRefusal =
    write.reason ??
    (targetsUnread
      ? "The Editor Service did not report which Summary targets exist, so a prompt cannot be created until that read succeeds."
      : targets.isPending
        ? "Still reading which Summary targets exist."
        : null);

  const createControl = (
    <Tooltip content={createRefusal ?? `Create a new prompt in ${sectionLabel}`}>
      <Button
        variant="outline"
        size="sm"
        disabled={createRefusal !== null}
        onClick={() => setCreating(true)}
      >
        <Plus aria-hidden="true" className="h-4 w-4" />
        New Summary prompt
      </Button>
    </Tooltip>
  );

  return (
    <>
      <LibrarySectionHeader
        sectionLabel={sectionLabel}
        description={sectionDescription}
        actions={
          <>
            {createControl}
            {/* The house toggle form: a constant label, so `aria-pressed` reads
                as a toggle rather than as a second button, and the variant
                carries the visible state. */}
            <Button
              variant={showArchived ? "primary" : "outline"}
              size="sm"
              aria-pressed={showArchived}
              onClick={() => setShowArchived((value) => !value)}
            >
              Show archived
            </Button>
          </>
        }
      />

      <section
        aria-label={`${sectionLabel} library assets`}
        className="space-y-4"
      >
        {/* A held refresh over cached rows is still a failed refresh: a parked
            retry keeps `status: "success"` with `isError: false`, so testing the
            settled half alone would leave rows of unknown age presenting as
            current. */}
        {assets.data ? (
          <StaleDataNotice
            updatedAt={assets.dataUpdatedAt}
            onRetry={retryAssets}
            visible={
              assets.isPaused ||
              (assets.isError && !isTerminalQueryError(assets.error))
            }
          />
        ) : null}

        <PartialDataNotice
          visible={targetsUnread}
          description="The Editor Service did not report which Summary targets this runtime reads, so no new prompt can be created and the exact pack file is not shown under Details. The prompts below and their targets are unaffected."
          {...(targetsUnread && !isTerminalQueryError(targetsFailure)
            ? {
                action: (
                  <Button variant="outline" onClick={retryTargets}>
                    Try again
                  </Button>
                ),
              }
            : {})}
        />

        {assets.isPaused && assets.data === undefined ? (
          <NamedReadFailureState
            failure={assets.failureReason}
            isPaused
            retry={retryAssets}
            subject="Library assets"
            title="Library assets could not be loaded"
            description="The Editor Service did not return the shared clinical library."
            deniedDescription="You are not authorized to read the shared clinical library."
            offlineDescription="The shared clinical library could not be reached. It loads on its own when the connection returns."
          />
        ) : assets.isPending ? (
          <LoadingState label={`Loading ${sectionLabel} assets`} rows={4} />
        ) : assets.isError &&
          (!assets.data || isTerminalQueryError(assets.error)) ? (
          <QueryErrorState
            error={assets.error}
            subject="Library assets"
            title="Library assets could not be loaded"
            description="The Editor Service did not return the shared clinical library."
            deniedDescription="You are not authorized to read the shared clinical library."
            onRetry={() => void assets.refetch()}
          />
        ) : items.length === 0 ? (
          <EmptyState
            title={`No Summary prompts in ${sectionLabel} yet`}
            description={
              showArchived
                ? "No Summary prompt exists in the library."
                : "No active Summary prompt is in this section. Archived prompts stay hidden until shown."
            }
            action={createControl}
          />
        ) : (
          <div className="space-y-6">
            <SummaryGroup
              title="Report sections"
              description="One prompt per named part of the report. Each writes the section's own text file."
              rows={sections}
              targets={targets.data}
              showArchived={showArchived}
              onCopy={setCopying}
            />
            <SummaryGroup
              title="Diagnostic findings"
              description="One prompt per evaluation, inside the report's diagnostic-findings section. Which conditions exist comes from the mounted clinical pack."
              rows={diagnostics}
              targets={targets.data}
              showArchived={showArchived}
              onCopy={setCopying}
            />
            {unreadable.length > 0 ? (
              <SummaryGroup
                title="Without a readable target"
                description="These prompts carry a stored placement this editor cannot read as a Summary target, so which prompt each one is cannot be stated here."
                rows={unreadable}
                targets={targets.data}
                showArchived={showArchived}
                onCopy={setCopying}
              />
            ) : null}
          </div>
        )}
      </section>

      {creating ? (
        <NewSummaryPromptDialog onClose={() => setCreating(false)} />
      ) : null}
      {copying ? (
        <NewSummaryPromptDialog
          copyFrom={copying}
          onClose={() => setCopying(null)}
        />
      ) : null}
    </>
  );
}

function SummaryGroup({
  title,
  description,
  rows,
  targets,
  showArchived,
  onCopy,
}: {
  title: string;
  description: string;
  rows: readonly SummaryPromptAsset[];
  targets: SummaryTargetCatalog | undefined;
  showArchived: boolean;
  onCopy: (asset: SummaryPromptAsset) => void;
}) {
  // View state only, and reset by nothing: the row set for one group is small
  // and the bound exists so a curated-for-a-year archive cannot render
  // unboundedly in one click.
  const [pages, setPages] = useState(1);
  const visible = rows.slice(0, pages * PAGE_SIZE);
  const hidden = rows.length - visible.length;

  return (
    <CatalogGroup title={title} description={description}>
      {rows.length === 0 ? (
        // Said, not left blank: an empty group is a different fact from a
        // section with nothing in it.
        <p className="rounded-md border border-border px-5 py-3 text-sm text-muted-foreground">
          {showArchived
            ? `No ${title.toLowerCase()} exist in the library.`
            : `No active ${title.toLowerCase()} are in this section. Archived prompts stay hidden until shown.`}
        </p>
      ) : (
        <Card className="@container overflow-hidden">
          {visible.map((asset) => (
            <SummaryRow
              key={asset.id}
              asset={asset}
              targets={targets}
              onCopy={() => onCopy(asset)}
            />
          ))}
          {hidden > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-5 py-3 text-sm text-muted-foreground">
              <span aria-live="polite">
                Showing {visible.length} of {rows.length}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPages((value) => value + 1)}
              >
                Show {Math.min(hidden, PAGE_SIZE)} more
              </Button>
            </div>
          ) : null}
        </Card>
      )}
    </CatalogGroup>
  );
}

function SummaryRow({
  asset,
  targets,
  onCopy,
}: {
  asset: SummaryPromptAsset;
  targets: SummaryTargetCatalog | undefined;
  onCopy: () => void;
}) {
  const adoption = asset.capabilities.adoptionCount;
  const target = asset.summaryTarget ?? null;
  const directory = target ? directoryFor(targets, target) : null;

  return (
    <CatalogRow
      to={libraryAssetHref(asset.assetType, asset.id)}
      name={asset.name}
      titleAdornment={<AssetStateBadges capabilities={asset.capabilities} />}
      meta={[
        // WHICH PROMPT THIS IS, first and in the pack's own spelling. The
        // reference's single column is this slug; humanising it would give one
        // clinical thing a second name.
        <span key="target" className="font-mono text-sm text-foreground">
          {target ? summaryTargetName(target) : "No readable target"}
        </span>,
        <span key="version" className="font-mono">
          {asset.version}
        </span>,
        <span key="author" className="min-w-0 truncate">
          {`Created by ${asset.createdBy}`}
        </span>,
        <span key="updated">{`Updated ${formatDate(asset.updatedAt)}`}</span>,
        <span key="adoption">
          {`Used by ${adoption} ${adoption === 1 ? "project" : "projects"}`}
        </span>,
      ]}
      actions={
        asset.capabilities.seeded ? (
          // The service's own refusal for seeded canon says to copy it; this is
          // that copy as an action rather than a dead badge explaining why the
          // row cannot be edited.
          <Tooltip content="Part of the base clinical library. The original stays as it is — make an editable copy to create your own version.">
            <Button
              variant="ghost"
              size="sm"
              className="relative z-10"
              onClick={onCopy}
            >
              Create editable copy
            </Button>
          </Tooltip>
        ) : undefined
      }
      details={
        // The shell's own disclosure, whose accessible name carries the subject
        // — a catalog of rows all named "Details" is the repeated-ambiguous-
        // control defect the parity audit already recorded once.
        <CatalogDetails subject={asset.name}>
          <dl className="grid gap-0.5 font-mono text-[11px] text-muted-foreground">
            <div className="break-all">
              pack placement: {asset.placementCoordinate ?? "not stated"}
            </div>
            <div className="break-all">
              writes:{" "}
              {directory ??
                "not reported — the Summary target catalog could not be read, or this runtime does not register this target"}
            </div>
          </dl>
        </CatalogDetails>
      }
    />
  );
}

/**
 * The exact pack directory this target writes, as the SERVER expands it.
 *
 * `null` rather than a joined path when the catalog has not arrived or no longer
 * offers this target: a directory shown here is a claim about the file the
 * freeze writes, and the only authority for that claim is the server's own
 * template expansion. A target the mounted pack stopped registering genuinely
 * has no such file, and saying so is the point.
 */
function directoryFor(
  targets: SummaryTargetCatalog | undefined,
  target: NonNullable<SummaryPromptAsset["summaryTarget"]>,
): string | null {
  if (target.kind === "section") {
    return (
      targets?.sections.find((option) => option.section === target.section)
        ?.directory ?? null
    );
  }
  return (
    evaluationsOfGroup(targets, target.group).find(
      (option) => option.evaluation === target.evaluation,
    )?.directory ?? null
  );
}
